import { ANIMATION_DURATIONS } from '../config/animationConfig.js';

/**
 * Utility class for card animations
 * Provides reusable animation functions for PixiJS sprites
 */
class CardAnimations {
    // Track all active animation frame IDs
    static activeAnimations = new Set();

    /**
     * Cancel all active animations
     * Call this when cleaning up to prevent animations from running after sprites are destroyed
     */
    static cancelAllAnimations() {
        this.activeAnimations.forEach(id => {
            if (id) {
                cancelAnimationFrame(id);
            }
        });
        this.activeAnimations.clear();
    }
    /**
     * Animates a card flip with a squeeze effect (scale X compression)
     * @param {PIXI.Sprite} sprite - The sprite to animate
     * @param {PIXI.Texture} newTexture - The texture to show after flip
     * @param {number} finalRotation - The final rotation in radians (0 or Math.PI)
     * @param {number} duration - Animation duration in milliseconds (default: 300)
     * @returns {Promise} Resolves when animation completes
     */
    static flipCard(sprite, newTexture, finalRotation = 0, duration = ANIMATION_DURATIONS.CARD_FLIP) {
        return new Promise((resolve) => {
            // Validate sprite exists and is not destroyed
            if (!sprite || sprite.destroyed || !sprite.anchor || !sprite.scale) {
                console.warn('[CardAnimations] flipCard: Invalid or destroyed sprite');
                resolve();
                return;
            }

            const start = performance.now();

            // Store original state with additional validation
            let originalState;
            try {
                originalState = {
                    anchor: { x: sprite.anchor.x, y: sprite.anchor.y },
                    position: { x: sprite.x, y: sprite.y },
                    scale: { x: sprite.scale.x, y: sprite.scale.y },
                    rotation: sprite.rotation,
                    width: sprite.width,
                    height: sprite.height
                };
            } catch (error) {
                console.warn('[CardAnimations] flipCard: Error storing original state', error);
                resolve();
                return;
            }

            // Calculate center position based on current anchor
            let centerX, centerY;
            try {
                centerX = sprite.x + (0.5 - sprite.anchor.x) * sprite.width;
                centerY = sprite.y + (0.5 - sprite.anchor.y) * sprite.height;

                // Set anchor to center for scale animation
                sprite.anchor.set(0.5);
                sprite.x = centerX;
                sprite.y = centerY;
            } catch (error) {
                console.warn('[CardAnimations] flipCard: Error setting anchor position', error);
                resolve();
                return;
            }

            let animationId = null;
            const animate = (time) => {
                // Validate sprite still exists and has required properties
                if (!sprite || sprite.destroyed || !sprite.scale || !sprite.anchor || !sprite.parent) {
                    if (animationId) CardAnimations.activeAnimations.delete(animationId);
                    resolve();
                    return;
                }

                try {
                    const t = Math.min(1, (time - start) / duration);

                    // First half: compress to 0
                    if (t < 0.5) {
                        const s = 1 - t / 0.5;
                        sprite.scale.x = originalState.scale.x * s;
                        sprite.scale.y = originalState.scale.y;
                    } else {
                        // Swap texture at midpoint
                        if (t >= 0.5 && sprite.texture !== newTexture) {
                            sprite.texture = newTexture;
                            // Restore size
                            sprite.width = originalState.width;
                            sprite.height = originalState.height;
                            // Apply rotation if needed
                            sprite.rotation = finalRotation;
                        }

                        // Second half: expand from 0 to 1
                        const s = (t - 0.5) / 0.5;
                        sprite.scale.x = Math.max(0.01, originalState.scale.x * s);
                        sprite.scale.y = originalState.scale.y;
                    }

                    if (t < 1) {
                        animationId = requestAnimationFrame(animate);
                        CardAnimations.activeAnimations.add(animationId);
                    } else {
                        // Ensure final state
                        sprite.scale.x = originalState.scale.x;
                        sprite.scale.y = originalState.scale.y;
                        sprite.rotation = finalRotation;
                        sprite.width = originalState.width;
                        sprite.height = originalState.height;

                        // Keep anchor at center since the update() method will reposition it
                        // The sprite position stays at center
                        if (animationId) CardAnimations.activeAnimations.delete(animationId);
                        resolve();
                    }
                } catch (error) {
                    console.warn('[CardAnimations] flipCard: Error during animation', error);
                    if (animationId) CardAnimations.activeAnimations.delete(animationId);
                    resolve();
                }
            };

            animationId = requestAnimationFrame(animate);
            CardAnimations.activeAnimations.add(animationId);
        });
    }

    /**
     * Animates a card reveal (from back to front)
     * @param {Object} cardObj - Object with back and front sprites
     * @param {PIXI.Sprite} cardObj.back - The back sprite
     * @param {PIXI.Sprite} cardObj.front - The front sprite
     * @param {number} duration - Animation duration in milliseconds (default: 300)
     * @returns {Promise} Resolves when animation completes
     */
    static revealCard(cardObj, duration = ANIMATION_DURATIONS.CARD_REVEAL) {
        return new Promise((resolve) => {
            // Validate sprites exist and are not destroyed
            if (!cardObj || !cardObj.back || !cardObj.front ||
                cardObj.back.destroyed || cardObj.front.destroyed) {
                console.warn('[CardAnimations] revealCard: Invalid or destroyed sprites');
                resolve();
                return;
            }

            const spriteBack = cardObj.back;
            const spriteFront = cardObj.front;
            const start = performance.now();

            // Get base scales
            const base = cardObj._baseScale || {
                backX: spriteBack.scale.x || 1,
                backY: spriteBack.scale.y || 1,
                frontX: spriteFront.scale.x || 1,
                frontY: spriteFront.scale.y || 1
            };

            // Ensure anchors are centered
            try {
                spriteBack.anchor && spriteBack.anchor.set && spriteBack.anchor.set(0.5);
            } catch (e) { }
            try {
                spriteFront.anchor && spriteFront.anchor.set && spriteFront.anchor.set(0.5);
            } catch (e) { }

            // Initially hide front
            spriteFront.visible = false;
            spriteFront.scale.x = base.frontX * 0.01;

            let animationId = null;
            const animate = (time) => {
                if (!spriteBack || !spriteFront ||
                    spriteBack.destroyed || spriteFront.destroyed ||
                    !spriteBack.parent || !spriteFront.parent) {
                    if (animationId) CardAnimations.activeAnimations.delete(animationId);
                    resolve();
                    return;
                }
                const t = Math.min(1, (time - start) / duration);

                // First half: scale back X to 0
                if (t < 0.5) {
                    const s = 1 - t / 0.5;
                    spriteBack.scale.x = base.backX * s;
                    spriteBack.scale.y = base.backY;
                    spriteFront.scale.x = base.frontX * 0.01;
                    spriteFront.scale.y = base.frontY;
                } else {
                    // Swap visible at midpoint
                    spriteBack.visible = false;
                    spriteFront.visible = true;

                    // Second half: scale front X from 0 to 1
                    const s = (t - 0.5) / 0.5;
                    spriteFront.scale.x = Math.max(0.01, base.frontX * s);
                    spriteFront.scale.y = base.frontY;
                }

                if (t < 1) {
                    animationId = requestAnimationFrame(animate);
                    CardAnimations.activeAnimations.add(animationId);
                } else {
                    // Ensure final state
                    spriteBack.visible = false;
                    spriteFront.visible = true;
                    spriteFront.scale.x = base.frontX;
                    spriteFront.scale.y = base.frontY;
                    if (animationId) CardAnimations.activeAnimations.delete(animationId);
                    resolve();
                }
            };

            animationId = requestAnimationFrame(animate);
            CardAnimations.activeAnimations.add(animationId);
        });
    }

    /**
     * Animates a card hide (from front to back)
     * @param {Object} cardObj - Object with back and front sprites
     * @param {PIXI.Sprite} cardObj.back - The back sprite
     * @param {PIXI.Sprite} cardObj.front - The front sprite
     * @param {number} duration - Animation duration in milliseconds (default: 300)
     * @returns {Promise} Resolves when animation completes
     */
    static hideCard(cardObj, duration = ANIMATION_DURATIONS.CARD_HIDE) {
        return new Promise((resolve) => {
            // Validate sprites exist and are not destroyed
            if (!cardObj || !cardObj.back || !cardObj.front ||
                cardObj.back.destroyed || cardObj.front.destroyed) {
                console.warn('[CardAnimations] hideCard: Invalid or destroyed sprites');
                resolve();
                return;
            }

            const spriteBack = cardObj.back;
            const spriteFront = cardObj.front;
            const start = performance.now();

            // Get base scales
            const base = cardObj._baseScale || {
                backX: spriteBack.scale.x || 1,
                backY: spriteBack.scale.y || 1,
                frontX: spriteFront.scale.x || 1,
                frontY: spriteFront.scale.y || 1
            };

            // Ensure anchors are centered
            try {
                spriteBack.anchor && spriteBack.anchor.set && spriteBack.anchor.set(0.5);
            } catch (e) { }
            try {
                spriteFront.anchor && spriteFront.anchor.set && spriteFront.anchor.set(0.5);
            } catch (e) { }

            // Initially hide back
            spriteBack.visible = false;
            spriteBack.scale.x = base.backX * 0.01;

            let animationId = null;
            const animate = (time) => {
                if (!spriteBack || !spriteFront ||
                    spriteBack.destroyed || spriteFront.destroyed ||
                    !spriteBack.parent || !spriteFront.parent) {
                    if (animationId) CardAnimations.activeAnimations.delete(animationId);
                    resolve();
                    return;
                }
                const t = Math.min(1, (time - start) / duration);

                // First half: scale front X to 0
                if (t < 0.5) {
                    const s = 1 - t / 0.5;
                    spriteFront.scale.x = base.frontX * s;
                    spriteFront.scale.y = base.frontY;
                    spriteBack.scale.x = base.backX * 0.01;
                    spriteBack.scale.y = base.backY;
                } else {
                    // Swap visible at midpoint
                    spriteFront.visible = false;
                    spriteBack.visible = true;

                    // Second half: scale back X from 0 to 1
                    const s = (t - 0.5) / 0.5;
                    spriteBack.scale.x = Math.max(0.01, base.backX * s);
                    spriteBack.scale.y = base.backY;
                }

                if (t < 1) {
                    animationId = requestAnimationFrame(animate);
                    CardAnimations.activeAnimations.add(animationId);
                } else {
                    // Ensure final state
                    spriteFront.visible = false;
                    spriteBack.visible = true;
                    spriteBack.scale.x = base.backX;
                    spriteBack.scale.y = base.backY;
                    if (animationId) CardAnimations.activeAnimations.delete(animationId);
                    resolve();
                }
            };

            animationId = requestAnimationFrame(animate);
            CardAnimations.activeAnimations.add(animationId);
        });
    }

    /**
     * Animates a smooth slide/move of a sprite
     * @param {PIXI.Sprite} sprite - The sprite to animate
     * @param {Object} targetPos - Target position {x, y}
     * @param {number} duration - Animation duration in milliseconds (default: 400)
     * @param {Function} easing - Easing function (default: easeInOutQuad)
     * @returns {Promise} Resolves when animation completes
     */
    static slideCard(sprite, targetPos, duration = ANIMATION_DURATIONS.CARD_SLIDE, easing = null) {
        return new Promise((resolve) => {
            // Validate sprite exists and is not destroyed
            if (!sprite || sprite.destroyed) {
                console.warn('[CardAnimations] slideCard: Invalid or destroyed sprite');
                resolve();
                return;
            }

            const start = performance.now();
            let startPos;
            try {
                startPos = { x: sprite.x, y: sprite.y };
            } catch (error) {
                console.warn('[CardAnimations] slideCard: Error reading sprite position', error);
                resolve();
                return;
            }

            // Default easing function (ease in-out quadratic)
            const ease = easing || ((t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

            let animationId = null;
            const animate = (time) => {
                if (!sprite || sprite.destroyed || !sprite.parent) {
                    if (animationId) CardAnimations.activeAnimations.delete(animationId);
                    resolve();
                    return;
                }

                try {
                    const t = Math.min(1, (time - start) / duration);
                    const easedT = ease(t);

                    sprite.x = startPos.x + (targetPos.x - startPos.x) * easedT;
                    sprite.y = startPos.y + (targetPos.y - startPos.y) * easedT;

                    if (t < 1) {
                        animationId = requestAnimationFrame(animate);
                        CardAnimations.activeAnimations.add(animationId);
                    } else {
                        // Ensure final position
                        sprite.x = targetPos.x;
                        sprite.y = targetPos.y;
                        if (animationId) CardAnimations.activeAnimations.delete(animationId);
                        resolve();
                    }
                } catch (error) {
                    console.warn('[CardAnimations] slideCard: Error during animation', error);
                    if (animationId) CardAnimations.activeAnimations.delete(animationId);
                    resolve();
                }
            };

            animationId = requestAnimationFrame(animate);
            CardAnimations.activeAnimations.add(animationId);
        });
    }
}

export default CardAnimations;
